import classnames from "classnames";
import {
  AnalyticsComponent,
  Button,
  IssueCardModal,
  IssueForm,
  ListMap,
  PromptModal,
  CategoryPillBar,
} from "components";
import { MapIcon, ViewListIcon } from "@heroicons/react/outline";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { ReactComponent as SquareLeft } from "assets/images/square-left.svg";
import { ReactComponent as SquareRight } from "assets/images/square-right.svg";
import { useNavigate, useOutletContext } from "react-router-dom";
import { getRating, getSearchParams } from "utils";
import { useResizeDetector } from "react-resize-detector";
import { IAISuggestions, IOutletAuth } from "types";
import { useWindowDimensions, useIssueRatingCategories } from "hooks";
import { IssueCard } from "./IssueCard";
import { useIssueListFilterParams } from "./IssueList.hooks";
import JumboImage1 from "assets/images/jumbo-1.png";
import { SlideOver } from "components/SlideOver";
import { APP_ROUTES } from "const";
import { Dialog } from "@headlessui/react";
import { useQueryClient } from "react-query";

function IssueList() {
  const navigate = useNavigate();
  const authContext = useOutletContext<IOutletAuth>();
  const [showGoSignInModal, setShowGoSignInModal] = useState(false);

  const [open, setOpen] = useState(false);
  const [openCardModal, setOpenCardModal] = useState(false);
  const [openedCardId, setOpenedCardId] = useState<string | undefined>(
    undefined
  );
  const { width, height, ref: mapRef } = useResizeDetector();
  const [isIssueHovered, setIsIssueHovered] = useState(false);

  const [hoveredIssueId, setHoveredIssueId] = useState<
    string | undefined | string[]
  >(undefined);
  const [hoveredIssueType, setHoveredIssueType] = useState<
    "regular" | "city" | "country" | undefined
  >();

  const [issueHoveredProps, setIssueHoveredProps] = useState({
    lat: NaN,
    lng: NaN,
  });
  const dim = useWindowDimensions();
  const disabled = useMemo(() => {
    return !!(open && dim.width && dim.width <= 1023);
  }, [dim.width, open]);

  const { data, inViewRef, setQueryOptions, searchParams } = useIssueListFilterParams(
    width || dim.width,
    disabled
  );

  // Category filtering
  const { data: categoriesData } = useIssueRatingCategories();
  const categories = categoriesData?.data || [];

  const selectedCategories = useMemo(() => {
    const params = getSearchParams(searchParams);
    return Array.isArray(params.category)
      ? params.category
      : params.category
      ? [params.category]
      : [];
  }, [searchParams]);

  const handleCategoryToggle = useCallback(
    (categoryId: string) => {
      setQueryOptions((prev: any) => {
        const existing = Array.isArray(prev.category)
          ? prev.category
          : prev.category
          ? [prev.category]
          : [];
        const hasCategory = existing.includes(categoryId);
        return {
          ...prev,
          category: hasCategory
            ? existing.filter((item: string) => item !== categoryId)
            : [...existing, categoryId],
        };
      });
    },
    [setQueryOptions]
  );

  const handleClearCategories = useCallback(() => {
    setQueryOptions((prev: any) => ({
      ...prev,
      category: [],
    }));
  }, [setQueryOptions]);

  const [openFormReport, setOpenFormReport] = useState(false);
  const [showReportPromptModal, setShowReportPromptModal] = useState(false);
  const [createdIssueId, setCreatedIssueId] = useState<string | undefined>(
    undefined
  );
  const [showSuccessReportPromptModal, setShowSuccessReportPromptModal] =
    useState(false);
  useEffect(() => {
    if (createdIssueId) {
      setShowSuccessReportPromptModal(true);
    }
  }, [createdIssueId]);

  const [, setMutationCacheRerenderCounter] = useState(0);

  const queryClient = useQueryClient();

  const subscribeCacheCallback = useCallback((queryCacheNotifyEvent) => {
    if (!queryCacheNotifyEvent) {
      return;
    }
    setMutationCacheRerenderCounter((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const unsubscribe = queryClient
      .getMutationCache()
      .subscribe(subscribeCacheCallback);
    return () => unsubscribe();
  }, [queryClient, subscribeCacheCallback]);
  const allMutations = queryClient
    .getMutationCache()
    .findAll({ mutationKey: ["createAIAnalyticsIssue"] }) as any[];
  const latestAIMutation = useMemo(
    () =>
      allMutations && allMutations.length > 0
        ? //@ts-ignore
          allMutations[allMutations.length - 1]?.state?.data?.data
        : //@ts-ignore
          allMutations[0]?.state?.data?.data,
    [allMutations]
  ) as IAISuggestions | undefined;

  const moreFeedbackQuality = useMemo(() => {
    if (!latestAIMutation) {
      return undefined;
    }
    return latestAIMutation.more_feedback;
  }, [latestAIMutation]);

  return (
    <div className={classnames("flex flex-col relative h-full pb-32 md:pb-20 lg:pb-12")}>
      <div
        className={classnames(
          "grid lg:grid-cols-[1fr_1fr_1fr_1.3fr]   h-full lg:h-[calc(100vh-118px)]  lg:mr-0 gap-4    "
          // { "ml-[5%] mr-[5%] ": !open },
          // { "lg:ml-[5%]": open }
        )}
      >
        <div
          className={classnames(
            "  one-fourth-step three-fourth-step ten-fourth-step eleven-third-step grid grid-cols-1 auto-rows-max gap-4 lg:overflow-scroll pb-4 pt-[3px] pl-[3px]    pr-2 max-w-[2000px] transition-all duration-500 ease-linear",
            {
              "sm:grid-cols-2 lg:grid-cols-3 lg:col-span-3": !open,
              "hidden lg:grid max-w-[500px]": open,
            }
          )}
        >
          <div
            className={classnames(
              " relative    flex-1 max-h-[300px]  w-full  overflow-hidden",
              {
                "flex col-span-1 sm:col-span-2  lg:col-span-3": !open,
              },
              {
                hidden: open,
              }
            )}
          >
            <div className=" flex justify-center flex-1 w-full h-full">
              <img
                style={{ flexBasis: "100%" }}
                className="select-none pointer-events-none min-h-[120px] sm:min-h-[180px] flex-1 w-full h-full object-contain"
                alt="jumbo"
                src={authContext.backgroundImages || JumboImage1}
              />
            </div>
          </div>

          {/* Category Pill Bar with Action Buttons */}
          {!open && categories.length > 0 && (
            <div className="col-span-1 sm:col-span-2 lg:col-span-3 -mt-2 mb-2">
              <CategoryPillBar
                categories={categories}
                selectedCategories={selectedCategories}
                onCategoryToggle={handleCategoryToggle}
                onClearCategories={handleClearCategories}
                allLabel="All Issues"
                actionButton={
                  authContext?.isAuth && (
                    <button
                      type="button"
                      onClick={() => setShowReportPromptModal(true)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
                      style={{
                        background: "#C2410C",
                        fontFamily: "'Source Sans 3', sans-serif",
                        boxShadow: "0 4px 12px -2px rgba(194, 65, 12, 0.3)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#A13508";
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow =
                          "0 6px 16px -2px rgba(194, 65, 12, 0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#C2410C";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px -2px rgba(194, 65, 12, 0.3)";
                      }}
                    >
                      + Report Issue
                    </button>
                  )
                }
              />
            </div>
          )}

          {data?.pages.map((page, pagesIndex) => {
            return page.data.items.length < 1 ? (
              <div className="flex min-h-[400px] items-start justify-center col-span-6">
                <p className=" max-w-[700px] text-center text-xl">
                  There are no issues found for the chosen location, please
                  adjust your search parameters or
                  <span
                    onClick={() => {
                      if (!!authContext?.isAuth) {
                        setShowReportPromptModal(true);
                      }
                    }}
                    className="font-bold px-1 hover:text-white cursor-pointer"
                  >
                    report an issue
                  </span>
                </p>
              </div>
            ) : (
              page.data.items.map((issue, issueIndex) => {
                const displayLabel = issue?.location?.displayLabel;
                const isFormattedAddress = !!(
                  displayLabel || issue?.location?.formattedAddress
                );
                const isCountry = !!issue?.location?.country;
                const isCity = !!issue?.location?.city;
                const isStreetNumber = !!issue?.location?.streetNumber;
                const isRoute = !!issue?.location?.route;
                const location =
                  typeof issue?.location === "object"
                    ? isFormattedAddress
                      ? `${displayLabel || issue?.location?.formattedAddress}`
                      : isCountry && isCity && isStreetNumber && isRoute
                      ? `${issue?.location?.route} ${issue?.location?.streetNumber}, ${issue?.location?.city}, ${issue?.location?.country}`
                      : isCountry && isCity
                      ? ` ${issue?.location?.city}, ${issue?.location?.country}`
                      : isCountry
                      ? `${issue?.location?.country}`
                      : "No location"
                    : "No location";
                const geoLocation = {
                  lat: issue?.location?.lat,
                  lng: issue?.location?.lng,
                };
                const isLast =
                  data?.pages.length === pagesIndex + 1 &&
                  page.data.items.length === issueIndex + 1;

                const issueType =
                  issue?.location?.administrativeArea ||
                  issue?.location?.route ||
                  issue?.location?.streetNumber
                    ? "regular"
                    : issue?.location?.city
                    ? "city"
                    : "country";

                return (
                  <div
                    className={classnames(
                      "relative cursor-pointer   maxmd:flex  ",
                      {
                        " first-card-to-click ": issueIndex === 0,
                      }
                    )}
                    onClick={() => {
                      window.scrollTo(0, 0);
                      issue && setOpenedCardId(issue.id);
                      issue && setOpenCardModal(true);
                    }}
                    key={issue.id}
                  >
                    {issue.analyticsData && (
                      <AnalyticsComponent analyticsData={issue.analyticsData} />
                    )}
                    <IssueCard
                      id={issue.user?.id}
                      issueType={issueType}
                      setHoveredIssueType={setHoveredIssueType}
                      forceHoverIssue={hoveredIssueId?.includes(issue.id || "")}
                      setIsIssueHovered={setIsIssueHovered}
                      setIssueHoveredProps={setIssueHoveredProps}
                      geoLocation={geoLocation}
                      innerRef={isLast ? inViewRef : undefined}
                      title={issue.title}
                      //details={issue.details}
                      location={location}
                      profileImage={issue?.user?.avatar}
                      firstName={issue.user?.firstName}
                      lastName={issue.user?.lastName}
                      createdAt={issue.createdAt}
                      rating={getRating(issue.rating)}
                      primaryClassification={issue.primaryClassification}
                      secondaryClassification={issue.secondaryClassification}
                      email={issue?.user?.email}
                      score={issue.user?.reputationScore}
                      tags={issue?.tags}
                      isSoftDeleted={!!issue.deletedAt}
                    />
                  </div>
                );
              })
            );
          })}
        </div>
        <div
          className={classnames(
            "relative lg:rounded-lg lg:overflow-hidden",
            {
              "hidden lg:flex lg:flex-col row-span-2 row-start-1 lg:col-start-4":
                !open,
              "flex flex-col min-h-[60vh] h-[calc(100vh-200px)] lg:h-auto lg:col-start-2 lg:col-end-[-1]": open,
            }
          )}
        >
          <ListMap
            setOpenMap={setOpen}
            hoveredIssueType={hoveredIssueType}
            setHoveredIssueId={setHoveredIssueId}
            issueCardHovered={isIssueHovered}
            issueCardProps={issueHoveredProps}
            innerRef={mapRef}
            mapHeight={height || dim.height}
            mapWidth={width || dim.width}
          />

          <div
            className="flex  flex-col absolute top-7 left-5 cursor-pointer"
            onClick={() => setOpen((open) => !open)}
          >
            {!open && <SquareLeft />}
            {open && <SquareRight />}
          </div>
        </div>
      </div>

      {/* Floating Map/List Toggle - Mobile Only */}
      <button
        onClick={() => setOpen((open) => !open)}
        className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-5 py-2.5 rounded-full shadow-lg transition-all duration-300 ease-out lg:hidden active:scale-95"
        style={{
          background: 'rgba(250, 247, 242, 0.95)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(194, 65, 12, 0.2)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(194, 65, 12, 0.08)',
        }}
      >
        <span
          className="flex items-center justify-center w-6 h-6 rounded-full transition-colors duration-200"
          style={{
            background: 'rgba(194, 65, 12, 0.1)',
          }}
        >
          {open ? (
            <ViewListIcon className="w-4 h-4" style={{ color: '#C2410C' }} />
          ) : (
            <MapIcon className="w-4 h-4" style={{ color: '#C2410C' }} />
          )}
        </span>
        <span
          className="text-sm font-medium"
          style={{
            fontFamily: "'Source Sans 3', sans-serif",
            color: '#44403C',
          }}
        >
          {open ? "View List" : "View Map"}
        </span>
      </button>
      {openCardModal && openedCardId && (
        <IssueCardModal id={openedCardId} setShow={setOpenCardModal} />
      )}
      {showReportPromptModal && (
        <PromptModal
          setShow={setShowReportPromptModal}
          header="Report Issue"
          content="Before adding a new issue, please ensure a similar issue for this location does not exist already."
          footer={
            <div className="flex flex-row gap-2 justify-between w-full">
              <Button
                inGroupStyle={false}
                isStyleless
                isReverse
                className=" shrink-0 text-[black] border hover:border-black border-gray-500 "
                onClick={() => {
                  setShowReportPromptModal(false);
                  setOpenFormReport(true);
                }}
              >
                Continue, I have checked
              </Button>
              <Button
                inGroupStyle={false}
                isStyleless
                isReverse
                className=" text-[black] border hover:border-black border-gray-500 "
                onClick={() => {
                  setShowReportPromptModal(false);
                }}
              >
                Cancel
              </Button>
            </div>
          }
        />
      )}
      <div className="md:hidden h-24" aria-hidden />
      {!!showSuccessReportPromptModal && createdIssueId && (
        <PromptModal
          setShow={setShowSuccessReportPromptModal}
          header="Report Issue"
          content="Will you like to propose a solution for this issue?"
          footer={
            <div className="flex flex-row gap-2 justify-between w-full">
              <Button
                inGroupStyle={false}
                isStyleless
                isReverse
                className=" shrink-0 text-[black] border hover:border-black border-gray-500 "
                onClick={() => {
                  setShowSuccessReportPromptModal(false);
                  navigate(APP_ROUTES.ISSUE + "/" + createdIssueId);
                }}
              >
                Yes
              </Button>
              <Button
                inGroupStyle={false}
                isStyleless
                isReverse
                className=" text-[black] border hover:border-black border-gray-500 "
                onClick={() => {
                  setShowSuccessReportPromptModal(false);
                  setCreatedIssueId(undefined);
                }}
              >
                Maybe later
              </Button>
            </div>
          }
        />
      )}
      <SlideOver
        id="issue-report"
        open={openFormReport}
        close={useCallback(() => {
          setCreatedIssueId(undefined);
          setOpenFormReport(false);
          queryClient.getMutationCache().clear();
        }, [queryClient])}
        title="Report Issue"
        description={
          <div className="flex flex-col">
            <Dialog.Description className="text-sm  text-gray-500 font-light mt-[14px]">
              This information will be displayed publicly so be careful what you
              share.
            </Dialog.Description>
            {(latestAIMutation?.feedback_areas?.length || 0) > 0 ? (
              <Dialog.Description className="text-sm  text-red-500 font-light">
                Please review the highlighted sections for suggested
                improvements before we can save your post
              </Dialog.Description>
            ) : null}
            {moreFeedbackQuality &&
            (moreFeedbackQuality.quality === "Useless" ||
              moreFeedbackQuality.quality === "Poor") ? (
              <Dialog.Description className="text-sm  text-red-500 font-light">
                {latestAIMutation?.more_feedback?.feedback}
              </Dialog.Description>
            ) : null}
          </div>
        }
      >
        <IssueForm
          onSuccess={useCallback(
            (id?: string) => {
              setCreatedIssueId(id);
              setOpenFormReport(false);
              queryClient.getMutationCache().clear();
            },
            [setOpenFormReport, setCreatedIssueId, queryClient]
          )}
          onClose={useCallback(() => {
            setCreatedIssueId(undefined);
            setOpenFormReport(false);
            queryClient.getMutationCache().clear();
          }, [setOpenFormReport, setCreatedIssueId, queryClient])}
        />
      </SlideOver>
      {showGoSignInModal && (
        <PromptModal
          setShow={setShowGoSignInModal}
          header="Warning"
          content="You have to sign in to perform this action"
          footer={
            <div className="flex flex-row gap-2 justify-between w-full">
              <Button
                inGroupStyle={false}
                isStyleless
                isReverse
                className=" shrink-0 text-[black] border hover:border-black border-gray-500 "
                onClick={() => {
                  setShowGoSignInModal(false);
                  setTimeout(() => {
                    navigate(APP_ROUTES.SIGN_IN);
                  }, 100);
                }}
              >
                Ok
              </Button>
              <Button
                inGroupStyle={false}
                isStyleless
                isReverse
                className=" text-[black] border hover:border-black border-gray-500 "
                onClick={(e) => {
                  e.stopPropagation();
                  setShowGoSignInModal(false);
                }}
              >
                Maybe later
              </Button>
            </div>
          }
        />
      )}
    </div>
  );
}

export default memo(IssueList);
